import { AppShell, Text, Title, Box, CloseButton } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import cn from 'clsx';
import { TermsOfUseModal } from 'src/components/TermsOfUseModal/TermsOfUseModal';
import { StakingStatusBanner } from 'src/components/StakingStatusBanner/StakingStatusBanner';
import { HEADER_LINKS } from 'src/constants';
import { LayoutHeader } from './LayoutHeader';
import { LayoutFooter } from './LayoutFooter';

const SHOW_APR_BOOST = import.meta.env.VITE_SHOW_APR_BOOST === 'true';

const PAD_X = 48;
const TARGET_WIDTH = 1120;
const MAX_WIDTH = TARGET_WIDTH + PAD_X * 2;

const HEADER_CONTENT_HEIGHT = 40;
const HEADER_PAD_Y = 16;
const HEADER_HEIGHT = HEADER_CONTENT_HEIGHT + HEADER_PAD_Y * 2;

const HERO_HEIGHT = 172;

const Section = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <AppShell.Section
      mx="auto"
      w={MAX_WIDTH}
      className={cn('relative max-w-full px-[4%] lg:px-12', className)}
    >
      {children}
    </AppShell.Section>
  );
};

type PageHeroType = {
  heading: string;
  description?: string;
  image?: string;
};

const _HERO_SECTIONS: Record<string, PageHeroType> = {
  portfolio: {
    heading: 'Portfolio',
    image: 'https://i.imgur.com/ei1wJp9.png',
  },
  ecosystem: {
    heading: 'Meet the APR-Boosted Teams',
    description: 'An Ecosystem that builds on aprMON.',
  },
};

const _STAKING_STATUS_BANNER_PAGES = ['', 'withdrawals'];

const HERO_SECTIONS = HEADER_LINKS.reduce(
  (acc, { to }) => {
    const pageName = to.slice(1);

    if (_HERO_SECTIONS[pageName]) {
      acc[pageName] = _HERO_SECTIONS[pageName];
    }

    return acc;
  },
  {} as Record<string, PageHeroType>
);

const PageHero = ({
  heading,
  description,
  image,
  bannerHeight = 0,
}: PageHeroType & { bannerHeight?: number }) => {
  return (
    <Box
      pt={HEADER_HEIGHT + bannerHeight}
      h={HERO_HEIGHT + HEADER_HEIGHT + bannerHeight}
      className={cn('relative bg-[#0F1012]', 'max-sm:!h-auto')}
    >
      <Section className="h-full">
        <div className="relative flex items-center gap-0 h-full max-sm:flex-col max-sm:pt-4">
          <div className="relative h-full flex flex-col justify-center lg:shrink-0">
            <Title order={2} fz={36} fw={700} c="#fff">
              {heading}
            </Title>
            {description && (
              <Text c="#A0A0B1" fz={18} fw={500} lh={1.2} className="mt-2">
                {description}
              </Text>
            )}
          </div>
          {image && (
            <div className="relative h-full min-w-0 lg:grow max-lg:hidden max-sm:block max-sm:w-full max-sm:h-[200px]">
              <img
                src={image}
                alt={heading}
                className="absolute right-0 bottom-0 max-h-full max-w-full max-sm:right-[50%] max-sm:translate-x-1/2"
              />
            </div>
          )}
        </div>
      </Section>
    </Box>
  );
};

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const pageName = location.pathname.split('/')[1];
  const pageHeroInfo = HERO_SECTIONS[pageName];
  const isStakingStatusBannerPage =
    _STAKING_STATUS_BANNER_PAGES.includes(pageName);
  const isLeaderboardPage = pageName === 'aprboost';
  const [showBanner, setShowBanner] = useState(SHOW_APR_BOOST);

  const bannerHeight = showBanner ? 38 : 0; // approximate height of banner with py-3

  return (
    <AppShell header={{ height: HEADER_HEIGHT }}>
      <TermsOfUseModal />

      {/* Top Banner */}
      {showBanner && (
        <div
          className="w-full bg-gradient-to-r from-primarybrand via-secondarybrand to-primarybrand h-[38px] flex items-center justify-center cursor-pointer relative"
          onClick={() => {
            window.location.pathname = '/aprboost';
          }}
        >
          <Text className="text-center text-[14px] font-semibold text-brandblack">
            Stake with Extra <span className="font-bold">$APR</span> Incentives
          </Text>
          <CloseButton
            onClick={(e) => {
              e.stopPropagation();
              setShowBanner(false);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-brandblack hover:bg-brandblack/10"
            size="sm"
          />
        </div>
      )}

      <AppShell.Header
        withBorder={false}
        className="absolute bg-transparent"
        style={{ top: bannerHeight }}
      >
        <Section className="h-full py-4">
          <LayoutHeader />
        </Section>
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[rgba(53,57,69,0.24)]" />
      </AppShell.Header>
      <AppShell.Main
        pt={!!pageHeroInfo ? 0 : undefined}
        className={cn(
          'transition-none',
          isLeaderboardPage &&
            'bg-[linear-gradient(180deg,#101010_75%,rgba(154,238,230,0.12)_100%)]'
        )}
      >
        {pageHeroInfo && (
          <PageHero {...pageHeroInfo} bannerHeight={bannerHeight} />
        )}
        <div className={cn('py-10', pageHeroInfo && 'pt-8')}>
          <Section>
            {isStakingStatusBannerPage && <StakingStatusBanner />}
            {children}
          </Section>
        </div>
      </AppShell.Main>
      <AppShell.Footer className="relative border-t-white/[0.14]">
        <Section className="h-full py-6">
          <LayoutFooter />
        </Section>
      </AppShell.Footer>
    </AppShell>
  );
}
